/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { motion } from "motion/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { MonitorAgent, getMonitorAgentHardwareStats, getMonitorAgentStatus } from "@/api/monitor";
import { cn } from "@/lib/utils";
import { Bar, StatusDot, fmtPct, fmtRate } from "./kit";
import { useAgentOnline } from "./ServerCard";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

type SortKey = "attention" | "name" | "cpu" | "ram" | "disk" | "net";

interface Row {
  agent: MonitorAgent;
  state: "up" | "down" | "idle";
  cpu?: number;
  ram?: number;
  disk?: number;
  rx?: number;
  tx?: number;
  host: string;
}

const COLS: { key: SortKey; label: string }[] = [
  { key: "name", label: "Server" },
  { key: "cpu", label: "CPU" },
  { key: "ram", label: "RAM" },
  { key: "disk", label: "Disk" },
  { key: "net", label: "Network" },
];

/** One dense row per server, sortable; the default order puts trouble first. */
export const ServerTable: React.FC<{
  agents: MonitorAgent[];
  selectedId?: number;
  onSelect: (id: number) => void;
}> = ({ agents, selectedId, onSelect }) => {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "attention", desc: true });

  const hw = useQueries({
    queries: agents.map((a) => ({
      queryKey: ["server-table-hw", a.id],
      queryFn: () => getMonitorAgentHardwareStats(a.id),
      enabled: a.enabled,
      refetchInterval: 20_000,
      staleTime: 10_000,
      retry: false,
    })),
  });
  const st = useQueries({
    queries: agents.map((a) => ({
      queryKey: ["server-table-status", a.id],
      queryFn: () => getMonitorAgentStatus(a.id),
      enabled: a.enabled,
      refetchInterval: 20_000,
      staleTime: 10_000,
      retry: false,
    })),
  });

  const rows: Row[] = useMemo(
    () =>
      agents.map((agent, i) => {
        const h = hw[i]?.data;
        const s = st[i]?.data;
        const online = !!h || useAgentOnline(s);
        return {
          agent,
          state: !agent.enabled ? "idle" : online ? "up" : "down",
          cpu: h?.cpu?.usage_percent,
          ram: h?.memory?.used_percent,
          disk: h?.disks?.length ? Math.max(...h.disks.map((d) => d.used_percent)) : undefined,
          rx: s?.liveData?.rx.bytespersecond,
          tx: s?.liveData?.tx.bytespersecond,
          host: (agent.url || "").replace(/^https?:\/\//, "").replace(/\/.*$/, ""),
        } as Row;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agents, hw.map((q) => q.dataUpdatedAt).join(), st.map((q) => q.dataUpdatedAt).join()],
  );

  const sorted = useMemo(() => {
    const val = (r: Row): number | string => {
      switch (sort.key) {
        case "name": return r.agent.name.toLowerCase();
        case "cpu": return r.cpu ?? -1;
        case "ram": return r.ram ?? -1;
        case "disk": return r.disk ?? -1;
        case "net": return (r.rx ?? 0) + (r.tx ?? 0);
        default: {
          // offline first, then the hottest resource
          const heat = Math.max(r.cpu ?? 0, r.ram ?? 0, r.disk ?? 0);
          return (r.state === "down" ? 1000 : r.state === "idle" ? -10 : 0) + heat;
        }
      }
    };
    return [...rows].sort((a, b) => {
      const [x, y] = [val(a), val(b)];
      const c = typeof x === "string" ? x.localeCompare(y as string) : (x as number) - (y as number);
      return sort.desc ? -c : c;
    });
  }, [rows, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: key !== "name" }));

  return (
    <div className="sv-table" role="table" aria-label="Servers">
      <div className="sv-trow sv-thead" role="row">
        {COLS.map((c) => (
          <button
            key={c.key}
            type="button"
            role="columnheader"
            className={cn("sv-th", sort.key === c.key && "sv-th--on", c.key !== "name" && c.key !== "net" && "sv-th--meter")}
            onClick={() => toggle(c.key)}
          >
            {c.label}
            {sort.key === c.key && (sort.desc ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronUpIcon className="h-3 w-3" />)}
          </button>
        ))}
      </div>
      {sorted.map((r) => (
        <motion.button
          layout="position"
          transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
          key={r.agent.id}
          type="button"
          role="row"
          className={cn("sv-trow sv-tr", `sv-tr--${r.state}`, selectedId === r.agent.id && "sv-tr--sel")}
          onClick={() => onSelect(r.agent.id)}
        >
          <span className="sv-tname">
            <StatusDot state={r.state} />
            <span className="sv-tname-txt">
              <b>{r.agent.name}</b>
              <small>{r.state === "idle" ? "disabled" : r.state === "down" ? "offline" : r.host}</small>
            </span>
          </span>
          {([r.cpu, r.ram, r.disk] as (number | undefined)[]).map((v, i) => (
            <span key={i} className="sv-tmeter">
              {v === undefined ? (
                <em>—</em>
              ) : (
                <>
                  <Bar pct={v} warn={i === 2 ? 85 : 75} crit={i === 2 ? 95 : 90} />
                  <b><AnimatedNumber value={v} mountFrom={null} format={(n) => fmtPct(n)} /></b>
                </>
              )}
            </span>
          ))}
          <span className="sv-tnet">
            {r.rx === undefined ? <em>—</em> : `↓ ${fmtRate(r.rx)} ↑ ${fmtRate(r.tx ?? 0)}`}
          </span>
        </motion.button>
      ))}
    </div>
  );
};
